import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, BedDouble, Building2, Car, ChevronLeft, ChevronRight, Dumbbell,
  Eye, Home, Info, MapPin, Ruler, Share2, Sofa, UtensilsCrossed, Wind,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { ListingDetailsSkeleton } from "@/components/skeletons";
import { EmptyState } from "@/components/EmptyState";
import { supabase } from "@/integrations/supabase/client";
import {
  formatApartmentType,
  formatFurnished, formatPrice, formatPropertyType, type BuildingAge, type CurrencyCode, type PublicListing,
} from "@/lib/listings";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { viewingRequestSchema, normalizeWhatsapp } from "@/lib/validation";
import { PhoneInput } from "@/components/PhoneInput";
import { SEO, SITE_URL } from "@/lib/seo";
import { buildPublicListingUrl, buildWhatsAppUrl } from "@/lib/whatsapp";



const Spec = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm">
    <span className="inline-flex items-center gap-2 text-muted-foreground"><span className="text-primary">{icon}</span> {label}</span>
    <span className="font-bold">{value}</span>
  </div>
);

const ListingDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const [listing, setListing] = useState<PublicListing | null>(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const yn = (v: boolean | null | undefined) => (v ? t("details.yes") : t("details.no"));

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      const { data } = await supabase
        .from("public_listings")
        .select("*, listing_images(*)")
        .eq("id", id)
        .order("display_order", { referencedTable: "listing_images", ascending: true })
        .maybeSingle();
      setListing((data as PublicListing | null) ?? null);
      setLoading(false);
    };
    void load();
    void supabase.rpc("increment_listing_view", { _listing_id: id });
  }, [id]);

  if (loading) return <ListingDetailsSkeleton />;

  if (!listing) {
    return (
      <section className="min-h-screen bg-background px-4 py-12">
        <div className="mx-auto max-w-3xl">
          <EmptyState
            icon={Home}
            title={t("details.notFound")}
            ctaLabel={t("details.back")}
            ctaTo="/listings"
          />
        </div>
      </section>
    );
  }

  const images = listing.listing_images ?? [];
  const image = images[imageIndex];
  const currency = (listing.currency ?? "USD") as CurrencyCode;





  const seoTitle = `${listing.title} — ${listing.area} | ISTANOVA`.slice(0, 60);
  const seoDesc = (listing.description || listing.title || "").slice(0, 158);
  const heroImg = images[0]?.image_url;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: listing.title,
    description: listing.description || listing.title,
    url: `${SITE_URL}/listings/${listing.id}`,
    image: heroImg ? [heroImg] : undefined,
    datePosted: listing.created_at,
    offers: {
      "@type": "Offer",
      price: Number(listing.price),
      priceCurrency: currency,
      availability: listing.status === "available" ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
    },
    address: { "@type": "PostalAddress", addressLocality: "Istanbul", addressRegion: listing.area, addressCountry: "TR" },
    numberOfRooms: listing.rooms ?? undefined,
    floorSize: listing.size_sqm ? { "@type": "QuantitativeValue", value: Number(listing.size_sqm), unitCode: "MTK" } : undefined,
  };

  const handleShare = async () => {
    const url = buildPublicListingUrl(listing.id!);
    const text = `${listing.title} — ${t("share.message")}`;
    const shareData = { title: listing.title ?? "ISTANOVA", text, url };
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share(shareData);
        return;
      }
    } catch {
      // user cancelled or share failed — fall through to WhatsApp
    }
    window.open(buildWhatsAppUrl("", `${text} ${url}`), "_blank", "noopener,noreferrer");
  };

  return (
    <section className="min-h-screen bg-background px-4 py-10 sm:px-6 lg:px-8 pb-28 lg:pb-10">
      <SEO title={seoTitle} description={seoDesc} path={`/listings/${listing.id}`} image={heroImg} type="product" jsonLd={jsonLd} />
      <div className="mx-auto max-w-6xl">
        <Link to="/listings" className="mb-6 inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("details.back")}
        </Link>

        <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          {/* GALLERY */}
          <div>
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-surface-elevated">
              {image ? (
                <img src={image.image_url} alt={image.alt_text || listing.title || ""} className="h-full w-full object-cover" />
              ) : (
                <div className="grid h-full place-items-center text-muted-foreground"><Home className="h-12 w-12" /></div>
              )}
              {images.length > 1 && (
                <div className="absolute inset-x-3 top-1/2 flex -translate-y-1/2 justify-between">
                  <button type="button" aria-label={t("listings.prev")} onClick={() => setImageIndex((c) => (c === 0 ? images.length - 1 : c - 1))} className="grid h-10 w-10 place-items-center rounded-full bg-background/85 backdrop-blur hover:bg-secondary">{isRtl ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}</button>
                  <button type="button" aria-label={t("listings.next")} onClick={() => setImageIndex((c) => (c + 1) % images.length)} className="grid h-10 w-10 place-items-center rounded-full bg-background/85 backdrop-blur hover:bg-secondary">{isRtl ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}</button>
                </div>
              )}
              <div className="absolute start-3 top-3 flex gap-2">
                <span className="rounded-full bg-primary/90 px-3 py-1 text-xs font-black text-primary-foreground">
                  {listing.listing_type === "rent" ? t("details.forRent") : t("details.forSale")}
                </span>
                {listing.status && listing.status !== "available" && (
                  <span className="rounded-full bg-destructive/90 px-3 py-1 text-xs font-black text-destructive-foreground">
                    {t(`listingStatus.${listing.status}`)}
                  </span>
                )}
              </div>
            </div>
            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-5 gap-2 sm:grid-cols-8">
                {images.map((img, idx) => (
                  <button key={img.id} onClick={() => setImageIndex(idx)} className={`aspect-square overflow-hidden rounded-lg border ${idx === imageIndex ? "border-primary ring-2 ring-primary/40" : "border-border"}`}>
                    <img src={img.image_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-5">
            <div>
              <h1 className="text-3xl font-black tracking-tight sm:text-4xl">{listing.title}</h1>
              {(listing as { property_id?: string | null }).property_id && (
                <p className="mt-1 font-mono text-xs text-muted-foreground" dir="ltr">{(listing as { property_id?: string | null }).property_id}</p>
              )}
              <p className="mt-2 inline-flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" /> {listing.area}</p>
            </div>
            <div>
              <p className="text-3xl font-black text-primary">{formatPrice(Number(listing.price), currency)}</p>
              {(listing as { eligible_for_citizenship?: boolean }).eligible_for_citizenship && (
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1.5 text-sm font-black text-amber-950 shadow">
                  🇹🇷 {t("listing.badge.citizenship")}
                </span>
              )}
            </div>

            {listing.deposit_required && listing.deposit_amount != null && listing.listing_type === "rent" && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <p className="text-sm">
                  <strong>{t("details.depositRequired")}:</strong> {formatPrice(Number(listing.deposit_amount), (listing.deposit_currency ?? currency) as CurrencyCode)}
                </p>
                {listing.deposit_note && <p className="mt-1 text-xs italic text-muted-foreground">{listing.deposit_note}</p>}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <Spec
                icon={<BedDouble className="h-4 w-4" />}
                label={t("details.specType")}
                value={
                  <span className="inline-flex items-center gap-1">
                    {listing.apartment_type ? formatApartmentType(listing.apartment_type) : "-"}
                    {listing.apartment_type && ["1+1", "2+1", "3+1", "4+1", "5+2"].includes(listing.apartment_type) && (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" aria-label={t("field.apartmentType.tooltip")} className="text-muted-foreground hover:text-foreground">
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>{t("field.apartmentType.tooltip")}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                }
              />
              {(listing as { building_age?: string }).building_age && (
                <Spec icon={<Building2 className="h-4 w-4" />} label={t("listing.details.buildingAge")} value={t(`submit.buildingAge.options.${(listing as { building_age: BuildingAge }).building_age}`)} />
              )}
              <Spec icon={<Building2 className="h-4 w-4" />} label={t("details.specProperty")} value={listing.property_type ? formatPropertyType(listing.property_type) : "-"} />
              {listing.rooms != null && <Spec icon={<BedDouble className="h-4 w-4" />} label={t("details.specRooms")} value={String(listing.rooms)} />}
              {listing.bathrooms != null && <Spec icon={<BedDouble className="h-4 w-4" />} label={t("details.specBathrooms")} value={String(listing.bathrooms)} />}
              {listing.size_sqm != null && <Spec icon={<Ruler className="h-4 w-4" />} label={t("details.specSize")} value={`${listing.size_sqm} m²`} />}
              {listing.floor_number && <Spec icon={<Building2 className="h-4 w-4" />} label={t("details.specFloor")} value={listing.floor_number} />}
              {listing.building_type && <Spec icon={<Building2 className="h-4 w-4" />} label={t("details.specBuilding")} value={t(`building.${listing.building_type}`)} />}
              {listing.kitchen_type && <Spec icon={<UtensilsCrossed className="h-4 w-4" />} label={t("details.specKitchen")} value={t(`kitchen.${listing.kitchen_type}`)} />}
              <Spec icon={<Sofa className="h-4 w-4" />} label={t("details.specFurnished")} value={listing.furnished ? formatFurnished(listing.furnished) : "-"} />
              <Spec icon={<Wind className="h-4 w-4" />} label={t("details.specBalcony")} value={yn(listing.balcony)} />
              <Spec icon={<Car className="h-4 w-4" />} label={t("details.specParking")} value={yn(listing.parking)} />
              <Spec icon={<Dumbbell className="h-4 w-4" />} label={t("details.specGym")} value={yn(listing.has_gym)} />
              {listing.view ? <Spec icon={<Eye className="h-4 w-4" />} label={t("details.specView")} value={listing.view} /> : null}
            </div>

            {listing.apartment_type === "other" && listing.apartment_type_note && (
              <p className="text-sm italic text-muted-foreground">{listing.apartment_type_note}</p>
            )}

            {listing.description && <p className="whitespace-pre-line text-sm leading-7 text-muted-foreground">{listing.description}</p>}
            {listing.extra_notes && <p className="whitespace-pre-line rounded-lg border border-border bg-card p-3 text-sm text-muted-foreground">{listing.extra_notes}</p>}

            {typeof listing.view_count === "number" && listing.view_count > 0 && (
              <p className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <Eye className="h-4 w-4" /> {t("listing.views", { count: listing.view_count })}
              </p>
            )}

            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
              <DialogTrigger asChild>
                <button className="hidden lg:flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-base font-black text-primary-foreground shadow-premium hover:scale-[1.02]">
                  {t("details.requestViewing")}
                </button>
              </DialogTrigger>
              <RequestViewingDialog listingId={listing.id!} onDone={() => setModalOpen(false)} />
            </Dialog>
            <button
              type="button"
              onClick={handleShare}
              className="hidden lg:flex w-full items-center justify-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-bold text-foreground hover:bg-secondary"
            >
              <Share2 className="h-4 w-4" /> {t("share.title")}
            </button>
          </aside>
        </div>

        {/* LOCATION */}
        {listing.google_maps_url && (
          <div className="mt-10">
            <h2 className="mb-3 text-xl font-black">{t("details.approxLocation")}</h2>
            <a
              href={listing.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-black text-primary-foreground shadow-premium hover:scale-[1.02] transition"
            >
              <MapPin className="h-4 w-4" /> {t("listing.details.openMapButton")}
            </a>
          </div>
        )}
      </div>

      {/* STICKY MOBILE CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-border bg-background/95 p-3 backdrop-blur lg:hidden">
        <button
          onClick={() => setModalOpen(true)}
          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-base font-black text-primary-foreground"
        >
          {t("details.requestViewing")}
        </button>
        <button
          type="button"
          onClick={handleShare}
          aria-label={t("share.title")}
          className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-border bg-background text-foreground hover:bg-secondary"
        >
          <Share2 className="h-5 w-5" />
        </button>
      </div>

    </section>
  );
};

export default ListingDetails;

// =====================================================================
// Request Viewing Dialog
// =====================================================================
const RequestViewingDialog = ({ listingId, onDone }: { listingId: string; onDone: () => void }) => {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = viewingRequestSchema().safeParse({
      full_name: fullName,
      whatsapp_number: whatsapp,
      email,
      notes,
    });
    if (!parsed.success) {
      toast({ title: parsed.error.issues[0].message, variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("viewing_requests").insert({
      listing_id: listingId,
      full_name: fullName.trim(),
      whatsapp_number: normalizeWhatsapp(whatsapp),
      email: email.trim() || null,
      preferred_date: date || null,
      notes: notes.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      toast({ title: t("details.dialogError"), description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: t("details.dialogSuccess"), description: t("details.dialogSuccessDesc") });
    onDone();
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("modal.viewingTitle")}</DialogTitle>
        <p className="text-sm text-muted-foreground">{t("modal.viewingSubtitle")}</p>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t("details.dialogFullName")} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" required />
        <PhoneInput value={whatsapp} onChange={setWhatsapp} required />
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder={t("details.dialogEmail")} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
        <label className="block text-xs font-bold text-muted-foreground">
          {t("details.dialogPreferredDate")}
          <input value={date} onChange={(e) => setDate(e.target.value)} type="date" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-normal text-foreground" />
        </label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("details.dialogNotes")} rows={3} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
        <button disabled={submitting} className="w-full rounded-full bg-primary px-6 py-3 font-bold text-primary-foreground disabled:opacity-50">
          {submitting ? t("details.dialogSending") : t("details.dialogSend")}
        </button>
      </form>
    </DialogContent>
  );
};
